// use-delete-session.ts — Sessions-list suite, action.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { sessionsListKeys } from './keys';

const SESSION_DETAIL_KEY = {
  Session: 'session',
  SessionChat: 'session-chat',
  SessionProducts: 'session-products',
  SessionTeam: 'session-team',
} as const;

export function useDeleteSession(): ActionResult<{ sessionId: string }, void> {
  const api = useApi();
  const apiSessions = api.sessions;
  const queryClient = useQueryClient();

  return useActionHook<{ sessionId: string }, void>(
    useMemo(
      () => ({
        mutationFn: async ({ sessionId }) => {
          await apiSessions.byId(sessionId).delete();
        },
        onInvalidate: (_data, { sessionId }) => {
          // The session suite (Phase 5) will own these keys; for now mirror today's eviction.
          queryClient.removeQueries({ queryKey: [SESSION_DETAIL_KEY.Session, sessionId] });
          queryClient.removeQueries({ queryKey: [SESSION_DETAIL_KEY.SessionChat, sessionId] });
          queryClient.removeQueries({ queryKey: [SESSION_DETAIL_KEY.SessionProducts, sessionId] });
          queryClient.removeQueries({ queryKey: [SESSION_DETAIL_KEY.SessionTeam, sessionId] });
          queryClient.invalidateQueries({ queryKey: sessionsListKeys.all });
        },
      }),
      [apiSessions, queryClient],
    ),
  );
}
